import { useState, useEffect } from 'react';
import type { AdminThemePalette as AdminThemeToken } from '@baaligames/shared-ui/games/types';
import { getAdminAccessToken } from '@baaligames/shared-ui/admin/lib/adminApi';
import Icon from '@baaligames/shared-ui/components/Icon';
import ExplainedSetting from '@baaligames/shared-ui/admin/components/ExplainedSetting';
import { EXCHANGE_SETTING_SECTIONS, explainedPropsFromSpec } from '../../../components/settingPresets';
import CricketDashboard from './CricketDashboard';

interface ApiConfig {
  id: string;
  name: string;
  provider: string;
  url: string;
  key?: string;            // write-only: present only when the admin is typing a new key
  hasKey?: boolean;        // from GET — whether a key is stored server-side
  keyLast4?: string | null;// from GET — last 4 chars for identification, never the full key
  role: 'primary' | 'secondary' | 'none';
}

function getRelativeTime(dateString: string) {
  const d = new Date(dateString);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  if (diffMs < 0) return 'Just started';
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffMins = Math.floor((diffMs % 3600000) / 60000);
  if (diffHrs > 24) return `In ${Math.floor(diffHrs / 24)}d`;
  if (diffHrs > 0) return `In ${diffHrs}h ${diffMins}m`;
  return `In ${diffMins}m`;
}

function NumField({ label, value, onChange }: { label: string; value: any; onChange: (v: number) => void }) {
  return (
    <label className="text-sm">
      <span className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">{label}</span>
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? (undefined as any) : Number(e.target.value))}
        className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-900 dark:border-gray-600 text-gray-900 dark:text-white"
      />
    </label>
  );
}

// GM-4: this ONE ops panel serves all three API sports. `initialSport` scopes
// the header + defaults the RM slider tab so Football / Tennis get their own
// admin surface without duplicating the panel.
const SPORT_HEAD: Record<string, { emoji: string; label: string }> = {
  cricket: { emoji: '🏏', label: 'Cricket' },
  football: { emoji: '⚽', label: 'Football' },
  tennis: { emoji: '🎾', label: 'Tennis' },
};

export default function CricketAdminPanel({ theme, initialSport = 'cricket' }: { theme: AdminThemeToken; initialSport?: 'cricket' | 'football' | 'tennis' }) {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'matches' | 'api' | 'risk' | 'exchange'>('dashboard');
  const head = SPORT_HEAD[initialSport] || SPORT_HEAD.cricket;

  // Exchange control surface (P5)
  const [exCfg, setExCfg] = useState<any>({});
  const [exOverview, setExOverview] = useState<any>(null);
  const [exMsg, setExMsg] = useState('');
  const [agentRateId, setAgentRateId] = useState('');
  const [agentProfit, setAgentProfit] = useState('');
  const [agentComm, setAgentComm] = useState('');
  const [agentRateMsg, setAgentRateMsg] = useState('');

  // Live TV
  const [tvMatchId, setTvMatchId] = useState('');
  const [tvUrl, setTvUrl] = useState('');
  const [tvMsg, setTvMsg] = useState('');

  // Per-player limits
  const [limUserId, setLimUserId] = useState('');
  const [limMO, setLimMO] = useState('');
  const [limBM, setLimBM] = useState('');
  const [limFancy, setLimFancy] = useState('');
  const [limExposure, setLimExposure] = useState('');
  const [limMsg, setLimMsg] = useState('');

  // Fancy/session settlement
  const [fancyMatchId, setFancyMatchId] = useState('');
  const [fancySessions, setFancySessions] = useState<any[]>([]);
  const [fancyResults, setFancyResults] = useState<Record<string, string>>({});
  const [fancyMsg, setFancyMsg] = useState('');
  
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // For score updates
  const [manualTeam, setManualTeam] = useState('');
  const [manualRuns, setManualRuns] = useState('');
  const [manualWickets, setManualWickets] = useState('');
  const [currentOver, setCurrentOver] = useState('');

  // For manual settle
  const [winner, setWinner] = useState('');
  // 2026-07-16: settlement resolves by runner_id now, and per market. (The old
  // single `winner` text box was shared by EVERY market row on the page, and fed
  // free-text name-matching into the dead settle path.)
  const [winnerByMarket, setWinnerByMarket] = useState<Record<string, string>>({});
  
  // For API Settings (Pool Setup)
  const [apiPool, setApiPool] = useState<ApiConfig[]>([]);
  // Click-to-reveal the full stored key/URL (owner request, dev stage). The
  // full key is fetched on demand from the read-only reveal endpoint and held
  // in local state only — never persisted, never sent back on save.
  const [revealed, setRevealed] = useState<Record<string, { key: string; url: string }>>({});
  const [revealBusy, setRevealBusy] = useState<string | null>(null);
  async function toggleReveal(id: string) {
    if (revealed[id]) { setRevealed(p => { const n = { ...p }; delete n[id]; return n; }); return; }
    setRevealBusy(id);
    try {
      const r = await apiFetch(`/api/admin/cricket/settings/api/${encodeURIComponent(id)}/reveal`);
      setRevealed(p => ({ ...p, [id]: { key: r.key || '', url: r.url || '' } }));
    } catch (e) { console.error(e); }
    finally { setRevealBusy(null); }
  }
  async function copyText(text: string) {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard may be blocked; ignore */ }
  }
  const [strictVerification, setStrictVerification] = useState(false);
  const [autoFallback, setAutoFallback] = useState(false);
  // Cross-feed verification observability (read-only): matches the worker is auto-
  // holding because a second feed disagrees, and the most recent feed error.
  const [feedHeld, setFeedHeld] = useState<string[]>([]);
  const [feedLastError, setFeedLastError] = useState<{ at?: string; reason?: string; provider?: string; authFailure?: boolean } | null>(null);
  const [pollingInterval, setPollingInterval] = useState(3000);
  const [artificialDelay, setArtificialDelay] = useState(0);
  const [haltUpdates, setHaltUpdates] = useState(false);
  const [rmEnabled, setRmEnabled] = useState(false);
  const [rmShiftFactor, setRmShiftFactor] = useState(0.05);

  // Add new API Form State
  const [newApiName, setNewApiName] = useState('');
  const [newApiProvider, setNewApiProvider] = useState('CricAPI');
  const [newApiUrl, setNewApiUrl] = useState('');
  const [newApiKey, setNewApiKey] = useState('');

  useEffect(() => {
    fetchMatches();
    fetchApiSettings();
  }, []);

  async function apiFetch(url: string, options?: RequestInit) {
    const t = getAdminAccessToken();
    const res = await fetch(url, {
      ...options,
      headers: { 
        'Content-Type': 'application/json', 
        ...(t ? { authorization: `Bearer ${t}` } : {}),
        ...options?.headers 
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || 'API Error');
    }
    return res.json();
  }

  // Continuous Countdown Component
  const CountdownTimer = ({ dateStr }: { dateStr: string }) => {
    const [timeText, setTimeText] = useState('');
  
    useEffect(() => {
      const updateTime = () => {
        const diff = new Date(dateStr).getTime() - new Date().getTime();
        if (diff > 0 && diff < 24 * 60 * 60 * 1000) {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const secs = Math.floor((diff % (1000 * 60)) / 1000);
          
          let text = 'Starts in ';
          if (hours > 0) text += `${hours}h `;
          text += `${mins}m ${secs}s`;
          setTimeText(text);
        } else {
          setTimeText(`Starts: ${new Date(dateStr).toLocaleDateString([], { month: 'short', day: 'numeric' })} ${new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
        }
      };
      
      updateTime();
      const interval = setInterval(updateTime, 1000);
      return () => clearInterval(interval);
    }, [dateStr]);
  
    return (
      <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        {timeText}
      </span>
    );
  };

  async function fetchMatches() {
    try {
      setLoading(true);
      // GAP fix: scope the Live Matches list to THIS tab's sport, so the Football
      // tab shows only football (not cricket+tennis lumped together).
      const res = await apiFetch(`/api/admin/cricket/matches?sport=${initialSport}`);
      setMatches(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchApiSettings() {
    try {
      const res = await apiFetch('/api/admin/cricket/settings/api');
      setApiPool(res.api_pool || []);
      setStrictVerification(res.api_strict_verification === true || res.api_strict_verification === 'true');
      setAutoFallback(res.api_auto_fallback === true || res.api_auto_fallback === 'true');
      setFeedHeld(Array.isArray(res.feed_held_matches) ? res.feed_held_matches.map(String) : []);
      setFeedLastError(res.api_last_error && typeof res.api_last_error === 'object' ? res.api_last_error : null);
      if (res.api_polling_interval !== undefined) setPollingInterval(Number(res.api_polling_interval));
      if (res.api_artificial_delay !== undefined) setArtificialDelay(Number(res.api_artificial_delay));
      if (res.api_halt_updates !== undefined) setHaltUpdates(res.api_halt_updates === true || res.api_halt_updates === 'true');
      if (res.rm_enabled !== undefined) setRmEnabled(res.rm_enabled === true || res.rm_enabled === 'true');
      if (res.rm_shift_factor !== undefined) setRmShiftFactor(Number(res.rm_shift_factor));
    } catch (e) {
      console.error(e);
    }
  }

  async function saveApiSettings(updates?: any) {
    try {
      const payload = { 
        api_pool: apiPool,
        api_strict_verification: strictVerification,
        api_auto_fallback: autoFallback,
        api_polling_interval: pollingInterval,
        api_artificial_delay: artificialDelay,
        api_halt_updates: haltUpdates,
        rm_enabled: rmEnabled,
        rm_shift_factor: rmShiftFactor,
        ...updates
      };
      await apiFetch('/api/admin/cricket/settings/api', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (!updates) {
        alert('Settings saved successfully!');
      }
    } catch (e: any) {
      alert(e.message);
    }
  }

  function handleAddApi(e: React.FormEvent) {
    e.preventDefault();
    if (!newApiName || !newApiUrl) return;
    const isFirst = apiPool.length === 0;
    const newApi: ApiConfig = {
      id: Math.random().toString(36).substr(2, 9),
      name: newApiName,
      provider: newApiProvider,
      url: newApiUrl,
      key: newApiKey,
      role: isFirst ? 'primary' : 'secondary',
    };
    const updatedPool = [...apiPool, newApi];
    setApiPool(updatedPool);
    setNewApiName(''); setNewApiUrl(''); setNewApiKey('');
    saveApiSettings({ api_pool: updatedPool }); // Auto-save when adding
  }

  function handleRemoveApi(id: string) {
    const updatedPool = apiPool.filter(a => a.id !== id);
    setApiPool(updatedPool);
    saveApiSettings({ api_pool: updatedPool }); // Auto-save
  }

  function handleRoleChange(id: string, newRole: 'primary' | 'secondary' | 'none') {
    const updatedPool: ApiConfig[] = apiPool.map((a): ApiConfig => {
      if (a.id === id) {
        return { ...a, role: newRole };
      }
      // If setting a new primary, demote the old primary
      if (newRole === 'primary' && a.role === 'primary') {
        return { ...a, role: 'none' };
      }
      return a;
    });
    setApiPool(updatedPool);
    saveApiSettings({ api_pool: updatedPool }); // Auto-save role change
  }

  async function updateScore(matchId: string) {
    try {
      // C5 (2026-08-06): a partial score (e.g. only the over filled) must NOT be applied —
      // a blank score_string WIPES the stored live score (ON DUPLICATE KEY UPDATE) while
      // still falsely reporting 'Score updated'. Require the full score.
      if (!manualTeam || !manualRuns || !manualWickets) {
        alert('Enter team, runs, and wickets before applying a score.');
        return;
      }
      const scoreStr = `${manualTeam}: ${manualRuns}/${manualWickets}`;

      await apiFetch(`/api/admin/cricket/matches/${matchId}/score`, {
        method: 'POST',
        body: JSON.stringify({ score_string: scoreStr, current_over: currentOver })
      });
      alert('Score updated');
      fetchMatches();
      // Reset inputs
      setManualTeam('');
      setManualRuns('');
      setManualWickets('');
      setCurrentOver('');
    } catch (e: any) {
      alert(e.message);
    }
  }

  // 2026-07-16 FIX. This computed `'active'` — which is NOT a member of the
  // cricket_markets ENUM ('open','suspended','closed'). Real status is 'open', so
  // the ternary never matched and Suspend always sent 'active': it either errored
  // or wrote a value the engine reads as "not open", killing betting with no way
  // back (the UI still painted it green). It also POSTed the unvalidated legacy
  // route. Now: the validated + audited `exchange-status` route, and anything
  // not-'open' flips to 'open' so a market stuck in a bad state recovers here.
  async function toggleMarketStatus(_matchId: string, marketId: string, currentStatus: string) {
    const newStatus = currentStatus === 'open' ? 'suspended' : 'open';
    try {
      await apiFetch(`/api/admin/cricket/markets/${marketId}/exchange-status`, {
        method: 'POST',
        body: JSON.stringify({ status: newStatus, reason: 'admin manual toggle' })
      });
      fetchMatches();
    } catch (e: any) {
      alert(e.message);
    }
  }

  // 2026-07-16 FIX. This POSTed free-text {winner} to the legacy route, which reads
  // the dead `cricket_bets` table (zero rows since the P2 order-book rebuild) and
  // credits through the SHARED wallet — bypassing cricket's own ledgers, hashchain,
  // agent chain and exposure. It settled NOTHING, left real bets in
  // cricket_matched_bet untouched, closed the market, and still alerted
  // "Market settled successfully!". Now: the engine route `exchange-settle`, which
  // resolves by runner_id (never a name), suspends -> settles -> closes, and audits.
  // We report the REAL settled count instead of an unconditional success.
  async function settleMarket(matchId: string, marketId: string) {
    const runnerId = winnerByMarket[marketId];
    if (!runnerId) return alert('Pick the winning runner first.');
    const label = runnerLabel(matchId, marketId, runnerId);
    if (!confirm(`Settle this market with winner = ${label}?\n\nThis pays out real money and cannot be undone.`)) return;
    try {
      const r: any = await apiFetch(`/api/admin/cricket/matches/${matchId}/markets/${marketId}/exchange-settle`, {
        method: 'POST',
        body: JSON.stringify({ winningRunnerId: Number(runnerId), reason: 'admin manual settle' })
      });
      alert(`Settled ${r?.settled ?? 0} bet(s) — winner: ${label}.`);
      fetchMatches();
    } catch (e: any) {
      alert(e.message);
    }
  }

  // Void a market through the engine: every bet refunded, P&L zero. The engine
  // route already supported this (isVoid) — it just had no button.
  async function voidMarket(matchId: string, marketId: string) {
    if (!confirm('VOID this market?\n\nEvery bet on it is refunded and P&L is zero. This cannot be undone.')) return;
    try {
      const r: any = await apiFetch(`/api/admin/cricket/matches/${matchId}/markets/${marketId}/exchange-settle`, {
        method: 'POST',
        body: JSON.stringify({ isVoid: true, reason: 'admin manual void' })
      });
      alert(`Voided — ${r?.settled ?? 0} bet(s) refunded.`);
      fetchMatches();
    } catch (e: any) {
      alert(e.message);
    }
  }

  // Resolve a runner id to its display name for confirm dialogs.
  function runnerLabel(matchId: string, marketId: string, runnerId: string) {
    const mt = matches.find((x: any) => String(x.id) === String(matchId));
    const mk = mt?.markets?.find((x: any) => String(x.id) === String(marketId));
    const rn = mk?.runners?.find((x: any) => String(x.id) === String(runnerId));
    return rn?.display_name || `runner #${runnerId}`;
  }

  async function loadExchange() {
    try {
      const [cfg, ov] = await Promise.all([
        apiFetch('/api/admin/cricket/exchange/config'),
        apiFetch('/api/admin/cricket/exchange/overview'),
      ]);
      setExCfg(cfg || {});
      setExOverview(ov || null);
    } catch (e: any) { setExMsg(e.message); }
  }
  useEffect(() => { if (activeTab === 'exchange') loadExchange(); /* eslint-disable-next-line */ }, [activeTab]);

  async function saveExchangeConfig() {
    try {
      await apiFetch('/api/admin/cricket/exchange/config', { method: 'POST', body: JSON.stringify(exCfg) });
      setExMsg('Saved.'); loadExchange();
    } catch (e: any) { setExMsg(e.message); }
  }
  async function saveAgentRate() {
    if (!agentRateId) return setAgentRateMsg('Enter an agent id.');
    try {
      const res = await apiFetch(`/api/admin/cricket/exchange/agent-rate/${agentRateId}`, {
        method: 'POST',
        body: JSON.stringify({
          profitSharePct: agentProfit === '' ? undefined : Number(agentProfit),
          commissionSharePct: agentComm === '' ? undefined : Number(agentComm),
        }),
      });
      const clamps = (res.events || []).filter((e: any) => e.cause === 'cascade_clamp').length;
      setAgentRateMsg(`Saved: profit ${res.rate.profitSharePct}% / commission ${res.rate.commissionSharePct}%${clamps ? ` (cascade-clamped ${clamps} downline rate${clamps === 1 ? '' : 's'})` : ''}.`);
    } catch (e: any) { setAgentRateMsg(e.message); }
  }
  async function saveMatchStream() {
    if (!tvMatchId) return setTvMsg('Enter a match id.');
    try {
      const r = await apiFetch(`/api/admin/cricket/matches/${tvMatchId}/stream-url`, { method: 'POST', body: JSON.stringify({ url: tvUrl, reason: 'admin set' }) });
      setTvMsg(r.url ? `Stream set for match ${r.matchId}.` : `Stream cleared for match ${r.matchId}.`);
    } catch (e: any) { setTvMsg(e.message); }
  }
  async function loadUserLimits() {
    if (!limUserId) return setLimMsg('Enter a player id.');
    try {
      const r = await apiFetch(`/api/admin/cricket/exchange/user-limits/${limUserId}`);
      const l = r.limits || {};
      setLimMO(l.max_stake_match_odds != null ? String(l.max_stake_match_odds) : '');
      setLimBM(l.max_stake_bookmaker != null ? String(l.max_stake_bookmaker) : '');
      setLimFancy(l.max_stake_fancy != null ? String(l.max_stake_fancy) : '');
      setLimExposure(l.max_match_exposure != null ? String(l.max_match_exposure) : '');
      setLimMsg(r.limits ? 'Loaded.' : 'No personal limits set (global caps apply).');
    } catch (e: any) { setLimMsg(e.message); }
  }
  async function saveUserLimits() {
    if (!limUserId) return setLimMsg('Enter a player id.');
    try {
      const r = await apiFetch(`/api/admin/cricket/exchange/user-limits/${limUserId}`, {
        method: 'POST',
        body: JSON.stringify({
          maxStakeMatchOdds: limMO === '' ? null : Number(limMO),
          maxStakeBookmaker: limBM === '' ? null : Number(limBM),
          maxStakeFancy: limFancy === '' ? null : Number(limFancy),
          maxMatchExposure: limExposure === '' ? null : Number(limExposure),
          reason: 'admin set',
        }),
      });
      setLimMsg(`Saved for ${r.username}.`);
    } catch (e: any) { setLimMsg(e.message); }
  }
  async function loadFancySessions() {
    if (!fancyMatchId) return setFancyMsg('Enter a match id.');
    try {
      const res = await apiFetch(`/api/admin/cricket/exchange/fancy-open/${fancyMatchId}`);
      setFancySessions(res.sessions || []);
      setFancyMsg((res.sessions || []).length ? '' : 'No open sessions on this match.');
    } catch (e: any) { setFancyMsg(e.message); }
  }
  async function settleFancy(runnerId: number, doVoid = false) {
    try {
      if (doVoid) {
        const r = await apiFetch(`/api/admin/cricket/exchange/fancy-void/${runnerId}`, { method: 'POST', body: JSON.stringify({ reason: 'admin void' }) });
        setFancyMsg(`Voided ${r.voided} bet(s).`);
      } else {
        const val = fancyResults[String(runnerId)];
        if (val === undefined || val === '' || !Number.isFinite(Number(val))) return setFancyMsg('Enter the numeric session result first.');
        // Irreversible real-money payout — confirm the result first (audit 2026-08-05:
        // a mistyped result silently pays the wrong side with no undo).
        if (!confirm(`Settle this session at RESULT = ${val}?\n\nThis pays real money to the winning side (back wins if result ≥ its line, lay wins if below) and CANNOT be undone. Double-check the number.`)) return;
        const r = await apiFetch(`/api/admin/cricket/exchange/fancy-settle/${runnerId}`, { method: 'POST', body: JSON.stringify({ result: Number(val), reason: 'admin settle' }) });
        setFancyMsg(`Settled ${r.settled} bet(s) at result ${val}.`);
      }
      loadFancySessions();
    } catch (e: any) { setFancyMsg(e.message); }
  }
  const setCfg = (k: string, v: any) => setExCfg((c: any) => ({ ...c, [k]: v }));

  if (loading) return <div className="text-gray-900 dark:text-gray-100 p-8">Loading dashboard...</div>;

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{head.emoji} {head.label} — System Operations</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Automated API ingestion, market verification, and manual overrides — all three API sports share this engine &amp; books.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200 dark:border-gray-800 overflow-x-auto">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`pb-2 px-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'dashboard' ? `border-blue-500 text-blue-600 dark:text-blue-400` : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          Dashboard
        </button>
        <button
          onClick={() => setActiveTab('matches')}
          className={`pb-2 px-4 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'matches' ? `border-blue-500 text-blue-600 dark:text-blue-400` : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          Live Matches Dashboard
        </button>
        <button 
          onClick={() => setActiveTab('api')}
          className={`pb-2 px-4 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'api' ? `border-blue-500 text-blue-600 dark:text-blue-400` : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          API Integrations & Automation
        </button>
        <button 
          onClick={() => setActiveTab('risk')}
          className={`pb-2 px-4 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'risk' ? `border-blue-500 text-blue-600 dark:text-blue-400` : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          Risk Management
        </button>
        <button
          onClick={() => setActiveTab('exchange')}
          className={`pb-2 px-4 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'exchange' ? `border-blue-500 text-blue-600 dark:text-blue-400` : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`}
        >
          Exchange & Agents
        </button>
      </div>

      {/* EXCHANGE & AGENTS TAB (P5 control surface) */}
      {/* CRICKET DASHBOARD TAB — the control room (default landing) */}
      {activeTab === 'dashboard' && <CricketDashboard />}

      {activeTab === 'exchange' && (
        <div className="space-y-6 max-w-4xl">
          {/* Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">House P&amp;L (all sports)</div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">{exOverview ? exOverview.housePnl : '—'}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">Open worst-case liability</div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">{exOverview ? exOverview.openWorstCaseLiability : '—'}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">Open matched bets</div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">{exOverview ? exOverview.openMatchedBets : '—'}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">Agents with rates</div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">{exOverview ? exOverview.agentsWithRates : '—'}</div>
            </div>
          </div>

          {/* GAP-H1: completed-but-unsettled worklist (football/tennis have no result feed) */}
          <UnsettledMarketsControl />

          {/* GM-3: per-sport P&L split — cricket / football / tennis on their own lines */}
          <SportPnlControl />

          {/* DiamondExchange feed control — budget, live window, manual refresh, sports */}
          <DxFeedControl />

          {/* GM-5: RM Slider Engine — per-sport risk posture (Protection / Feed-Trust / Friendliness) */}
          <RmSliderControl initialSport={initialSport} />

          {/* Mode + commission + risk config */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Exchange &amp; Risk Controls</h2>
            {/* C4 (2026-08-06): precedence note. A sport's RM overlay (🎚️ Risk Management card
                above, stored as rm_overlay_<sport>) is Object.assign'd OVER these global values
                in getCricketConfig(sport), so for that sport the overlay — not this card — wins
                at bet time. Warn so nobody edits a value that never takes effect. */}
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700/60 p-3 text-xs text-amber-900 dark:text-amber-200">
              <b>These are the platform-wide defaults.</b> If a sport has an active overlay from the
              <b> 🎚️ Risk Management</b> card above, that sport uses its own overlay instead — for
              house margin, every exposure ceiling, the per-player per-match exposure cap, max-bet
              liability, odds-freshness, per-ball suspend, the in-play bet delay, the near-decided-match
              close and the odds-shift. For those sports the overlay is what actually wins at bet time,
              so a change here will not take effect — edit that sport with the sliders above. Mode, P2P
              commission, backstop liquidity, the per-player TOTAL (aggregate) exposure, broker credit
              limit and Global Halt are always taken from here (no overlay touches them).
            </div>
            <label className="text-sm block max-w-md">
              <span className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Default mode</span>
              <select value={exCfg.mode_default ?? 'house'} onChange={e => setCfg('mode_default', e.target.value)} className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-900 dark:border-gray-600 text-gray-900 dark:text-white">
                <option value="house">House (house is counterparty)</option>
                <option value="p2p">P2P (players match; commission only)</option>
                <option value="both">Both — player picks per bet (House or Exchange)</option>
              </select>
            </label>

            {/* Self-documenting preset settings (2026-08-06) — every game-deciding knob now
                explains itself (3 presets + custom + plain-language consequence), grouped by
                theme. Data-driven from EXCHANGE_SETTING_SECTIONS (settingPresets.ts); writes
                into exCfg and is saved by the same "Save controls" button below. */}
            {EXCHANGE_SETTING_SECTIONS.map((sec) => (
              <div key={sec.title} className="space-y-2">
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-4">{sec.title}</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {sec.specs.map((sp) => (
                    <ExplainedSetting key={sp.key} {...explainedPropsFromSpec(sp, exCfg[sp.key], (v) => setCfg(sp.key, v))} />
                  ))}
                </div>
              </div>
            ))}

            <div className="flex flex-col gap-2 mt-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!exCfg.rm_enabled} onChange={e => setCfg('rm_enabled', e.target.checked)} />
                <span className="text-gray-800 dark:text-gray-200">Risk-management enabled</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!exCfg.rm_global_halt} onChange={e => setCfg('rm_global_halt', e.target.checked)} />
                <span className="font-semibold text-red-600 dark:text-red-400">GLOBAL HALT (freeze all betting)</span>
              </label>
            </div>

            {/* Self-documenting risk control (2026-08-06) — presets + custom + plain-language
                consequences, so this near-decided-match guard is understandable at a glance.
                Writes into exCfg like every other control; saved by the same button below. */}
            <ExplainedSetting
              title="Close near-decided matches"
              help="When a team is almost sure to win, its price drops very low. This stops taking match-odds bets once the price reaches the level you pick — so nobody can back a near-certain winner at the last second."
              value={exCfg.decided_min_odds ?? 0}
              onChange={(v) => setCfg('decided_min_odds', v)}
              formatValue={(v) => (!v || Number(v) <= 0 ? 'Never close' : String(v))}
              presets={[
                { label: 'Never close', value: 0, tagline: 'Off', tone: 'risky',
                  consequence: `Betting stays open right up to the finish. Even a near-certain result can still be backed — a punter can grab a "sure thing" in the last moments and you pay out. Most turnover, least protection.` },
                { label: 'Balanced', value: 1.05, tagline: '1.05', tone: 'recommended',
                  consequence: `Match-odds betting stops once a team's price falls to 1.05 (about a 95% chance to win). Blocks last-second sure-thing bets while barely touching normal play. A good default for most operators.` },
                { label: 'Strict', value: 1.1, tagline: '1.10', tone: 'safe',
                  consequence: `Betting stops earlier, at a price of 1.10 (about a 91% chance). The safest against decided-match sniping — the market closes sooner, at the cost of a little late-game turnover.` },
              ]}
              custom={{
                kind: 'number', min: 1, max: 3, step: 0.01, unitSuffix: 'odds', placeholder: 'e.g. 1.05',
                describe: (v) => (!v || Number(v) <= 0
                  ? `Never closes early — betting stays open to the finish (no protection against sure-thing bets).`
                  : `Match-odds betting stops once a runner's price drops to ${v} or lower. Remember: a HIGHER number closes the market sooner (safer for you); a LOWER number lets punters bet on more-certain results (riskier).`),
              }}
            />

            <button onClick={saveExchangeConfig} className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded">Save controls</button>
            {exMsg && <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">{exMsg}</span>}
          </div>

          {/* Per-agent commission */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Agent commission (per-agent)</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">A child can never exceed its parent (Golden Limit); lowering a parent auto-clamps the downline.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="text-sm"><span className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Agent ID</span>
                <input value={agentRateId} onChange={e => setAgentRateId(e.target.value)} className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-900 dark:border-gray-600 text-gray-900 dark:text-white" /></label>
              <NumField label="Profit share %" value={agentProfit} onChange={(v) => setAgentProfit(String(v))} />
              <NumField label="Commission share %" value={agentComm} onChange={(v) => setAgentComm(String(v))} />
            </div>
            <button onClick={saveAgentRate} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded">Set agent rate</button>
            {agentRateMsg && <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">{agentRateMsg}</span>}
          </div>

          {/* Live TV */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">📺 Live TV — master switch (all sports)</h2>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={exCfg.livetv_enabled === true || exCfg.livetv_enabled === 'true'}
                  onChange={e => setCfg('livetv_enabled', e.target.checked)} className="w-4 h-4" />
                <span className={`text-sm font-bold ${(exCfg.livetv_enabled === true || exCfg.livetv_enabled === 'true') ? 'text-emerald-600' : 'text-gray-500'}`}>
                  {(exCfg.livetv_enabled === true || exCfg.livetv_enabled === 'true') ? 'ON — TV button shows on cricket, football & tennis' : 'OFF — TV button hidden everywhere (all sports)'}
                </span>
              </label>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">With the switch ON but no URL configured, players still see the TV player with a "can't connect right now" screen (useful in development). Switch OFF hides Live TV completely. Paste your TV-panel vendor's URL template — <code className="bg-gray-100 dark:bg-gray-900 px-1 rounded">{'{matchId}'}</code> is replaced per match (e.g. <code className="bg-gray-100 dark:bg-gray-900 px-1 rounded">https://vendor.tld/tv?eventid={'{matchId}'}</code>). Every in-play match then gets a "Watch Live TV" player automatically. A per-match URL below overrides the template. No URL → players see no TV section (never an empty box). https only.</p>
            <label className="text-sm block"><span className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Vendor URL template (all matches)</span>
              <input value={exCfg.livetv_url_template ?? ''} onChange={e => setCfg('livetv_url_template', e.target.value)}
                placeholder="https://vendor.tld/tv?eventid={matchId}"
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-900 dark:border-gray-600 text-gray-900 dark:text-white font-mono text-xs" />
            </label>
            <p className="text-[11px] text-gray-400">Saved with the exchange controls button above.</p>
            <div className="flex flex-wrap gap-3 items-end border-t border-gray-100 dark:border-gray-700 pt-3">
              <label className="text-sm"><span className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Match ID</span>
                <input value={tvMatchId} onChange={e => setTvMatchId(e.target.value)} className="px-3 py-2 border rounded bg-white dark:bg-gray-900 dark:border-gray-600 text-gray-900 dark:text-white" placeholder="e.g. 97634" /></label>
              <label className="text-sm flex-1 min-w-[240px]"><span className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Per-match stream URL (empty = clear)</span>
                <input value={tvUrl} onChange={e => setTvUrl(e.target.value)} className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-900 dark:border-gray-600 text-gray-900 dark:text-white font-mono text-xs" placeholder="https://…" /></label>
              <button onClick={saveMatchStream} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded">Save match stream</button>
              {tvMsg && <span className="text-sm text-gray-600 dark:text-gray-400">{tvMsg}</span>}
            </div>
          </div>

          {/* Per-player limits */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Player limits (per-player)</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Personal stake caps per market and a per-match exposure ceiling for one player. Empty = no personal cap (global limits still apply). Enforced at placement, inside the transaction.</p>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <label className="text-sm"><span className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Player ID</span>
                <div className="flex gap-2">
                  <input value={limUserId} onChange={e => setLimUserId(e.target.value)} className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-900 dark:border-gray-600 text-gray-900 dark:text-white" />
                  <button onClick={loadUserLimits} className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded">Load</button>
                </div></label>
              <NumField label="Max stake: Match Odds" value={limMO} onChange={(v) => setLimMO(String(v))} />
              <NumField label="Max stake: Bookmaker" value={limBM} onChange={(v) => setLimBM(String(v))} />
              <NumField label="Max stake: Session" value={limFancy} onChange={(v) => setLimFancy(String(v))} />
              <NumField label="Max exposure / match" value={limExposure} onChange={(v) => setLimExposure(String(v))} />
            </div>
            <button onClick={saveUserLimits} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded">Save player limits</button>
            {limMsg && <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">{limMsg}</span>}
          </div>

          {/* Fancy / session settlement */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Session (fancy) settlement</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Enter the session's ACTUAL result (e.g. runs at 10 overs). Every bet settles against its own frozen line: YES wins if result ≥ line, NO wins if result &lt; line. Sessions never decided → Void. Unsettled sessions auto-void 24h after the match completes.</p>
            <div className="flex gap-3 items-end">
              <label className="text-sm"><span className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 uppercase">Match ID</span>
                <input value={fancyMatchId} onChange={e => setFancyMatchId(e.target.value)} className="px-3 py-2 border rounded bg-white dark:bg-gray-900 dark:border-gray-600 text-gray-900 dark:text-white" placeholder="e.g. 97634" /></label>
              <button onClick={loadFancySessions} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold rounded">Load sessions</button>
              {fancyMsg && <span className="text-sm text-gray-600 dark:text-gray-400">{fancyMsg}</span>}
            </div>
            {fancySessions.length > 0 && (
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2">Session</th><th className="text-right py-2">NO line</th><th className="text-right py-2">YES line</th><th className="text-right py-2">Open bets</th><th className="text-right py-2">Stake</th><th className="text-right py-2">Result</th><th className="text-right py-2">Actions</th>
                </tr></thead>
                <tbody>
                  {fancySessions.map((s: any) => (
                    <tr key={s.runnerId} className="border-b border-gray-100 dark:border-gray-700/50">
                      <td className="py-2 font-semibold">{s.display_name}</td>
                      <td className="py-2 text-right font-mono">{s.lay_line ?? '—'}</td>
                      <td className="py-2 text-right font-mono">{s.back_line ?? '—'}</td>
                      <td className="py-2 text-right">{s.open_bets}</td>
                      <td className="py-2 text-right font-mono">₹{Number(s.open_stake).toLocaleString('en-IN')}</td>
                      <td className="py-2 text-right">
                        <input value={fancyResults[String(s.runnerId)] ?? ''} onChange={e => setFancyResults(r => ({ ...r, [String(s.runnerId)]: e.target.value }))}
                          className="w-20 px-2 py-1 border rounded text-right bg-white dark:bg-gray-900 dark:border-gray-600" placeholder="runs" />
                      </td>
                      <td className="py-2 text-right space-x-2">
                        <button onClick={() => settleFancy(s.runnerId)} className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded">Settle</button>
                        <button onClick={() => { if (confirm('Void every open bet on this session (full refunds)?')) settleFancy(s.runnerId, true); }} className="px-3 py-1 bg-red-600 hover:bg-red-500 text-white text-xs font-bold rounded">Void</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* MATCHES TAB */}
      {activeTab === 'matches' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
            <div>
              <h3 className="font-bold text-blue-800 dark:text-blue-300">System Automation Active</h3>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Matches are being ingested and verified automatically by the backend engine.</p>
            </div>
            <button onClick={fetchMatches} className={`px-4 py-2 text-sm font-semibold text-white rounded-md ${theme.accent} ${theme.accentHover}`}>
              Force Refresh
            </button>
          </div>

          {matches.length === 0 ? (
            <div className="p-8 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-center">
              <p className="text-gray-600 dark:text-gray-300">No active matches found in the database. Ensure your Primary API is correctly configured.</p>
            </div>
          ) : (
            matches.map(match => (
              <div key={match.id} className="bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    {match.team_a_img && <img src={match.team_a_img} alt="Team A" className="w-8 h-8 object-cover rounded-full shadow-sm bg-white" />}
                    <div className="min-w-0">
                      <h3 className="font-bold text-lg text-gray-900 dark:text-white truncate">
                        {match.title}
                      </h3>
                      {/* Universal match ID (== EntitySport match_id). Admin-only. Click to copy —
                          this is the ID you pair with the Live-TV feed. */}
                      <button
                        type="button"
                        onClick={() => copyText(String(match.id))}
                        title="Universal match ID (EntitySport). Click to copy — pair this with the Live TV feed."
                        className="mt-1 inline-flex items-center gap-1.5 font-mono text-xs rounded px-2 py-0.5 border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        <span className="text-gray-400 dark:text-gray-500">Match ID</span>
                        <span className="text-gray-900 dark:text-white select-all">{match.id}</span>
                        <svg className="w-3 h-3 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      </button>
                    </div>
                    {match.team_b_img && <img src={match.team_b_img} alt="Team B" className="w-8 h-8 object-cover rounded-full shadow-sm bg-white" />}
                  </div>
                  <div className="flex items-center gap-3">
                    {match.status === 'in-play' ? (
                      <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 uppercase tracking-wider flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span> LIVE
                      </span>
                    ) : match.status === 'upcoming' ? (
                      <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 uppercase tracking-wider">
                        {getRelativeTime(match.start_time)}
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 uppercase tracking-wider">
                        {match.status}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column: Overrides */}
                  <div className="lg:col-span-1 space-y-4">
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Manual Score Override</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Manually update score if auto-sync fails</p>
                      <div className="space-y-3">
                        <select
                          value={manualTeam}
                          onChange={e => setManualTeam(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                        >
                          <option value="">Select Team</option>
                          <option value={match.team_a}>{match.team_a}</option>
                          <option value={match.team_b}>{match.team_b}</option>
                        </select>
                        
                        <div className="flex gap-2">
                          <input 
                            type="number" 
                            value={manualRuns} 
                            onChange={e => setManualRuns(e.target.value)} 
                            placeholder="Runs"
                            className="w-1/2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                          />
                          <input 
                            type="number" 
                            value={manualWickets} 
                            onChange={e => setManualWickets(e.target.value)} 
                            placeholder="Wickets"
                            className="w-1/2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                          />
                        </div>

                        <input 
                          type="text" 
                          value={currentOver} 
                          onChange={e => setCurrentOver(e.target.value)} 
                          placeholder="Overs (e.g. 15.2)"
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <button onClick={() => updateScore(match.id)} className={`w-full py-2 text-sm font-semibold text-white rounded-md ${theme.accent} ${theme.accentHover}`}>
                          Apply Score
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Markets */}
                  <div className="lg:col-span-2 space-y-3">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Live Markets Control</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Manage individual betting markets for this match. You can manually suspend betting if you spot an issue, or force-settle a market early by declaring a winner.</p>
                    </div>
                    {match.markets.map((m: any) => (
                      <div key={m.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg shadow-sm gap-4">
                        <div>
                          <div className="font-bold text-sm text-gray-900 dark:text-white">{m.market_name}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 capitalize flex items-center gap-2">
                            <span>{m.market_type}</span>
                            <span className="w-1 h-1 rounded-full bg-gray-400"></span>
                            {/* Only 'open' is live. 'active' is not a real status — painting it
                                green is exactly what hid the broken toggle from the admin. */}
                            <span className={m.status === 'open' ? 'text-green-600 dark:text-green-400 font-bold' : 'text-red-600 dark:text-red-400 font-bold'}>{m.status}</span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                          <div className="flex flex-col gap-1">
                            <button
                              title={m.status === 'open' ? "Immediately stop players from placing new bets on this market." : "Re-open this market for new bets."}
                              onClick={() => toggleMarketStatus(match.id, m.id, m.status)}
                              className={`px-3 py-1.5 text-xs font-bold rounded border transition-colors w-24 ${m.status === 'open' ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/40' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800 dark:hover:bg-green-900/40'}`}
                            >
                              {m.status === 'open' ? 'Suspend' : 'Activate'}
                            </button>
                            <span className="text-[10px] text-gray-500 max-w-[120px] leading-tight">Pauses/resumes betting on this market instantly.</span>
                          </div>

                          <div className="flex flex-col gap-1 border-l border-gray-200 dark:border-gray-700 pl-4">
                            <div className="flex items-center gap-2">
                              <select
                                title="Pick the winning runner. Settlement resolves by runner id — never by a typed name."
                                value={winnerByMarket[m.id] || ''}
                                onChange={e => setWinnerByMarket(prev => ({ ...prev, [m.id]: e.target.value }))}
                                className="w-36 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                              >
                                <option value="">Winner…</option>
                                {(m.runners || []).map((rn: any) => (
                                  <option key={rn.id} value={rn.id}>{rn.display_name}</option>
                                ))}
                              </select>
                              <button
                                title="Settle through the engine and pay out the winning runner."
                                onClick={() => settleMarket(match.id, m.id)}
                                className={`px-3 py-1.5 text-xs font-bold text-white rounded shadow-sm ${theme.accent} ${theme.accentHover}`}
                              >
                                Settle
                              </button>
                              <button
                                title="Void this market — every bet refunded, P&L zero."
                                onClick={() => voidMarket(match.id, m.id)}
                                className="px-3 py-1.5 text-xs font-bold rounded border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 dark:hover:bg-amber-900/40"
                              >
                                Void
                              </button>
                            </div>
                            <span className="text-[10px] text-gray-500 max-w-[220px] leading-tight">Settles through the engine by runner id (real payout, audited). Void refunds every bet.</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* API TAB */}
      {activeTab === 'api' && (
        <div className="space-y-6 max-w-6xl">
          
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            
            {/* Left Column: Pool */}
            <div className="xl:col-span-2 space-y-6">
              
              {/* API Pool List */}
              <div className="p-6 bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1">API Integrations Pool</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Manage your data providers. The automation engine will utilize these keys to fetch live scores and simulated odds.</p>

                {/* Cross-feed verification status — read-only observability. The worker
                    auto-holds a match (refusing bets, showing it suspended) when a
                    SECONDARY feed disagrees on the live score, and auto-releases when
                    they re-sync; the last feed error surfaces a dead/rejected token. */}
                <div className="mb-6 grid gap-3 sm:grid-cols-2">
                  <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Last feed error</div>
                    {feedLastError ? (
                      <div className="text-xs">
                        <div className={feedLastError.authFailure ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-amber-600 dark:text-amber-400'}>
                          {feedLastError.reason || 'error'}{feedLastError.authFailure ? ' · check the API token' : ''}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 mt-0.5">{[feedLastError.provider, feedLastError.at ? new Date(feedLastError.at).toLocaleString() : null].filter(Boolean).join(' · ')}</div>
                      </div>
                    ) : (
                      <div className="text-xs text-emerald-600 dark:text-emerald-400">No recent errors — feed healthy.</div>
                    )}
                  </div>
                  <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Auto-held matches (feed mismatch)</div>
                    {feedHeld.length ? (
                      <div className="text-xs text-rose-600 dark:text-rose-400 font-semibold break-words">{feedHeld.length} held — {feedHeld.join(', ')}</div>
                    ) : (
                      <div className="text-xs text-emerald-600 dark:text-emerald-400">None — feeds in sync (or single-feed).</div>
                    )}
                  </div>
                </div>

                {apiPool.length === 0 ? (
                  <div className="text-center p-8 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
                    <p className="text-gray-600 dark:text-gray-400 text-sm">No API endpoints configured. Add one below.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {apiPool.map(api => (
                      <div key={api.id} className={`flex flex-col md:flex-row items-start md:items-center justify-between p-4 rounded-xl border-2 transition-colors ${api.role === 'primary' ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10' : api.role === 'secondary' ? 'border-purple-500 bg-purple-50/50 dark:bg-purple-900/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
                        <div className="flex-1 mb-4 md:mb-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="font-bold text-base text-gray-900 dark:text-white">{api.name}</span>
                            <span className="px-2 py-0.5 text-[10px] font-bold bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded uppercase tracking-wider">{api.provider}</span>
                            {api.role === 'primary' && <span className="px-2 py-0.5 text-[10px] font-bold bg-blue-500 text-white rounded shadow-sm uppercase tracking-wider">Primary</span>}
                            {api.role === 'secondary' && <span className="px-2 py-0.5 text-[10px] font-bold bg-purple-500 text-white rounded shadow-sm uppercase tracking-wider">Secondary</span>}
                          </div>
                          {/* Full URL — shown untruncated once revealed, so the
                              admin can read the exact base URL end-to-end. */}
                          <div className={`text-sm text-gray-600 dark:text-gray-400 ${revealed[api.id] ? 'break-all font-mono' : 'truncate max-w-md'}`} title={api.url}>
                            {revealed[api.id] ? (revealed[api.id].url || api.url) : api.url}
                          </div>

                          {/* Key line — masked by default; click "Show" to fetch
                              + reveal the full stored key (read-only endpoint;
                              the stored key is never touched). */}
                          <div className="text-xs font-mono mt-1 flex items-center flex-wrap gap-2">
                            {revealed[api.id] ? (
                              <span className="text-amber-700 dark:text-amber-300 break-all">Key: {revealed[api.id].key || '(empty)'}</span>
                            ) : (
                              <span className="text-gray-500 dark:text-gray-500">Key: {api.hasKey ? `••••••••${api.keyLast4 || ''}` : 'None'}</span>
                            )}
                            {api.hasKey && (
                              <button
                                type="button"
                                onClick={() => toggleReveal(api.id)}
                                disabled={revealBusy === api.id}
                                className="px-2 py-0.5 text-[11px] font-bold rounded bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                              >
                                {revealBusy === api.id ? '…' : revealed[api.id] ? 'Hide' : 'Show'}
                              </button>
                            )}
                            {revealed[api.id] && (
                              <>
                                <button type="button" onClick={() => copyText(revealed[api.id].key)}
                                  className="px-2 py-0.5 text-[11px] font-bold rounded bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors">
                                  Copy key
                                </button>
                                <button type="button" onClick={() => copyText(revealed[api.id].url || api.url)}
                                  className="px-2 py-0.5 text-[11px] font-bold rounded bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors">
                                  Copy URL
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          {api.role !== 'primary' && (
                            <button 
                              onClick={() => handleRoleChange(api.id, 'primary')}
                              className="px-3 py-1.5 text-xs font-bold rounded bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                              Set Primary
                            </button>
                          )}
                          {api.role !== 'secondary' && (
                            <button 
                              onClick={() => handleRoleChange(api.id, 'secondary')}
                              className="px-3 py-1.5 text-xs font-bold rounded bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                              Set Secondary
                            </button>
                          )}
                          {api.role !== 'none' && (
                            <button 
                              onClick={() => handleRoleChange(api.id, 'none')}
                              className="px-3 py-1.5 text-xs font-bold rounded text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                            >
                              Unassign
                            </button>
                          )}
                          <button 
                            onClick={() => handleRemoveApi(api.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                            title="Delete API"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add New API */}
              <div className="p-6 bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-4">Add Configuration</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                  <div>
                    <label className="block text-xs font-bold mb-1.5 text-gray-700 dark:text-gray-300 uppercase tracking-wider">Display Name</label>
                    <input 
                      type="text" 
                      value={newApiName}
                      onChange={e => setNewApiName(e.target.value)}
                      placeholder="e.g. Main CricAPI Key"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1.5 text-gray-700 dark:text-gray-300 uppercase tracking-wider">Provider Type</label>
                    <select 
                      value={newApiProvider} 
                      onChange={e => {
                        const val = e.target.value;
                        setNewApiProvider(val);
                        if (val === 'EntitySports') setNewApiUrl('https://restapi.entitysport.com/v2/');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                    >
                      <option value="CricAPI">CricAPI (Live Scores)</option>
                      <option value="EntitySports">Entity Sports (Exchange)</option>
                      <option value="Betfair">Betfair (Odds)</option>
                      <option value="Diamond">Diamond API (Odds)</option>
                      <option value="Mock">Mock Simulation</option>
                      <option value="Custom">Custom Webhook</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold mb-1.5 text-gray-700 dark:text-gray-300 uppercase tracking-wider">Endpoint Base URL</label>
                    <input 
                      type="text" 
                      value={newApiUrl}
                      disabled={newApiProvider === 'EntitySports'}
                      onChange={e => setNewApiUrl(e.target.value)}
                      placeholder="https://api.cricapi.com/v1/"
                      className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none text-sm ${newApiProvider === 'EntitySports' ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold mb-1.5 text-gray-700 dark:text-gray-300 uppercase tracking-wider">Authentication Token / API Key</label>
                    <input 
                      type="text" 
                      value={newApiKey}
                      onChange={e => setNewApiKey(e.target.value)}
                      placeholder="Paste token here..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-mono"
                    />
                  </div>
                  <div className="md:col-span-2 pt-2">
                    <button 
                      onClick={handleAddApi}
                      className="px-6 py-2.5 text-sm font-bold text-white bg-green-600 hover:bg-green-500 rounded-md shadow-sm transition-colors"
                    >
                      Add to Pool
                    </button>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: Settings */}
            <div className="xl:col-span-1">
              <div className="p-6 bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm h-full">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">Automation Rules</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Configure how the backend engine handles data validation.</p>
                
                <div className="space-y-6">
                  {/* Toggle 1 */}
                  <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-sm text-gray-900 dark:text-white">Strict Verification</h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={strictVerification}
                          onChange={e => {
                            const val = e.target.checked;
                            setStrictVerification(val);
                            saveApiSettings({ api_strict_verification: val });
                          }}
                          className="sr-only peer" 
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      Requires both the Primary and Secondary API to return identical scores before updating the database. If a mismatch is detected, markets are automatically suspended to prevent incorrect payouts.
                    </p>
                  </div>

                  {/* Toggle 2 */}
                  <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-sm text-gray-900 dark:text-white">Auto-Fallback</h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={autoFallback}
                          onChange={e => {
                            const val = e.target.checked;
                            setAutoFallback(val);
                            saveApiSettings({ api_auto_fallback: val });
                          }}
                          className="sr-only peer" 
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      If the Primary API fails to respond or hits a rate limit, the system will instantly promote the Secondary API to Primary status to maintain live operations without admin intervention.
                    </p>
                  </div>

                  {/* Toggle 3 - Master Halt */}
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-sm text-red-900 dark:text-red-400 flex items-center gap-2">
                        <Icon name="block" size={16} /> Halt All Market Updates
                      </h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={haltUpdates}
                          onChange={e => {
                            const val = e.target.checked;
                            setHaltUpdates(val);
                            saveApiSettings({ api_halt_updates: val });
                          }}
                          className="sr-only peer" 
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-red-600"></div>
                      </label>
                    </div>
                    <p className="text-xs text-red-700 dark:text-red-300/80 leading-relaxed">
                      Emergency override. If enabled, the worker will immediately stop pushing any live updates from the API to the players. All markets will freeze at their current odds.
                    </p>
                  </div>
                </div>

                {/* Timing Controls */}
                <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-4">Timing & Delay Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                      <label className="block font-bold text-sm text-gray-900 dark:text-white mb-2">Polling Interval (ms)</label>
                      <input 
                        type="number" 
                        min="1000"
                        step="500"
                        value={pollingInterval}
                        onChange={e => setPollingInterval(Number(e.target.value))}
                        onBlur={() => saveApiSettings({ api_polling_interval: pollingInterval })}
                        className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm text-gray-900 dark:text-white mb-2 focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-600 dark:text-gray-400">How frequently the worker fetches new odds from the API. Default is 3000ms (3 seconds).</p>
                    </div>
                    
                    <div className="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                      <label className="block font-bold text-sm text-gray-900 dark:text-white mb-2">Artificial Delay (ms)</label>
                      <input 
                        type="number" 
                        min="0"
                        step="1000"
                        value={artificialDelay}
                        onChange={e => setArtificialDelay(Number(e.target.value))}
                        onBlur={() => saveApiSettings({ api_artificial_delay: artificialDelay })}
                        className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm text-gray-900 dark:text-white mb-2 focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-600 dark:text-gray-400">Adds an intentional delay before odds are shown to players to compensate for TV broadcast delays.</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* RISK MANAGEMENT TAB */}
      {activeTab === 'risk' && (
        <div className="space-y-6 max-w-4xl">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400">
                <Icon name="monitoring" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Risk Management & Auto-Balancing</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Automatically shift odds to mitigate heavy liabilities on one side of a market.</p>
              </div>
            </div>

            <div className="space-y-8">
              {/* Master Toggle */}
              <div className="flex items-center justify-between bg-purple-50 dark:bg-purple-900/10 p-4 rounded-lg border border-purple-100 dark:border-purple-800">
                <div>
                  <h4 className="font-bold text-sm text-purple-900 dark:text-purple-300">Enable Odds Auto-Balancing</h4>
                  <p className="text-xs text-purple-700 dark:text-purple-400/80 mt-1">When enabled, the system intercepts API odds and mathematically adjusts them if liability thresholds are breached.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={rmEnabled}
                    onChange={e => {
                      const val = e.target.checked;
                      setRmEnabled(val);
                      saveApiSettings({ rm_enabled: val });
                    }}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                </label>
              </div>

              {/* 2026-07-16: the "Liability Threshold (rm_liability_threshold)" field
                  was removed. It fed only the old odds-balancer that read the dead
                  cricket_bets table and was deleted with it — a knob wired to nothing.
                  The live odds-shift keys off the exposure ceiling on the Exchange tab
                  (rm_runner_exposure_ceiling), not a separate threshold here. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-gray-900 p-5 rounded-lg border border-gray-200 dark:border-gray-700">
                  <label className="block font-bold text-sm text-gray-900 dark:text-white mb-2">Shift Factor (Aggressiveness)</label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">How hard the odds move as a runner's exposure approaches its ceiling (set on the Exchange tab). At the ceiling, both prices shift by this fraction — 0.10 = up to 10%. 0 disables the shift.</p>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={rmShiftFactor}
                    onChange={e => setRmShiftFactor(Number(e.target.value))}
                    onBlur={() => saveApiSettings({ rm_shift_factor: rmShiftFactor })}
                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// DiamondExchange feed control — self-contained (own auth fetch via getAdminAccessToken).
// Budget meter + bounded "go live" window + manual refresh + per-sport toggles. Hits the
// /exchange/dx-* routes in adminCricket.routes.js.
function DxFeedControl() {
  const [st, setSt] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function dxFetch(url: string, options?: RequestInit) {
    const t = getAdminAccessToken();
    const res = await fetch(url, { ...options, headers: { 'Content-Type': 'application/json', ...(t ? { authorization: `Bearer ${t}` } : {}) } });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || d.success === false) throw new Error(d.error || `HTTP ${res.status}`);
    return d;
  }
  async function load() { try { setSt(await dxFetch('/api/admin/cricket/exchange/dx-status')); } catch (e: any) { setMsg(e.message); } }
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, []);

  async function goLive(minutes: number) { setBusy(true); setMsg(''); try { await dxFetch('/api/admin/cricket/exchange/dx-live', { method: 'POST', body: JSON.stringify({ minutes }) }); setMsg(minutes ? `Live window open for ${minutes} min.` : 'Live window closed.'); await load(); } catch (e: any) { setMsg(e.message); } finally { setBusy(false); } }
  async function refresh() { setBusy(true); setMsg('Refreshing…'); try { const r = await dxFetch('/api/admin/cricket/exchange/dx-refresh', { method: 'POST', body: JSON.stringify({}) }); const parts = Object.entries(r.result || {}).map(([s, v]: any) => `${s} ${v?.matches ?? 0}`); setMsg(`Refreshed — ${parts.join(', ')}. Budget left ${r.budget?.left}.`); await load(); } catch (e: any) { setMsg(e.message); } finally { setBusy(false); } }
  async function toggleSport(sport: string) { const cur: string[] = st?.sports || ['cricket']; const next = cur.includes(sport) ? cur.filter((s) => s !== sport) : [...cur, sport]; if (!next.length) return; try { await dxFetch('/api/admin/cricket/exchange/dx-sports', { method: 'POST', body: JSON.stringify({ sports: next }) }); await load(); } catch (e: any) { setMsg(e.message); } }

  const b = st?.budget || {};
  const usedPct = b.budget ? Math.min(100, Math.round((b.used / b.budget) * 100)) : 0;
  const liveNow = !!st?.liveNow;
  const mins = st?.liveUntil ? Math.max(0, Math.round((st.liveUntil - Date.now()) / 60000)) : 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-blue-400/40 dark:border-blue-500/30 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">🛰️ DiamondExchange Feed</h2>
        <span className={`text-xs font-bold px-2 py-1 rounded ${liveNow ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>{liveNow ? `● LIVE — ${mins} min left` : '○ Idle (budget-safe)'}</span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">The feed has a hard lifetime hit budget and is never auto-polled. Open a bounded live window for a demo (auto-closes) or refresh once. Cricket, Football &amp; Tennis flow through the same books.</p>

      <div>
        <div className="flex justify-between text-xs font-bold text-gray-600 dark:text-gray-300 mb-1"><span>API budget</span><span>{b.used ?? '—'} / {b.budget ?? '—'} used · {b.left ?? '—'} left</span></div>
        <div className="w-full h-2.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"><div className={`h-full ${usedPct > 85 ? 'bg-red-500' : usedPct > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${usedPct}%` }} /></div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs font-bold text-gray-500 uppercase mr-1">Go live:</span>
        {[15, 30, 60].map((m) => <button key={m} disabled={busy} onClick={() => goLive(m)} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-bold rounded">{m} min</button>)}
        <button disabled={busy || !liveNow} onClick={() => goLive(0)} className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 disabled:opacity-40 text-white text-sm font-bold rounded">Stop</button>
        <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
        <button disabled={busy} onClick={refresh} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-bold rounded">Refresh now</button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs font-bold text-gray-500 uppercase mr-1">Sports:</span>
        {[['cricket', '🏏 Cricket'], ['football', '⚽ Football'], ['tennis', '🎾 Tennis']].map(([id, label]) => {
          const on = (st?.sports || []).includes(id);
          return <button key={id} onClick={() => toggleSport(id)} className={`px-3 py-1.5 text-sm font-bold rounded border ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-900 text-gray-500 border-gray-300 dark:border-gray-600'}`}>{label}</button>;
        })}
      </div>

      {st?.lastError && <div className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 rounded p-2">Last feed error: {st.lastError.reason || JSON.stringify(st.lastError)}</div>}
      {msg && <div className="text-sm text-gray-600 dark:text-gray-300">{msg}</div>}
    </div>
  );
}

// GAP-H1: completed-but-unsettled worklist. Football/tennis have no result feed,
// so they can only be settled by an admin hand-picking the winner. This warns the
// operator BEFORE the worker's 24h auto-VOID grace refunds the bets. Renders
// nothing when the worklist is empty (the healthy state) — no idle noise.
function UnsettledMarketsControl() {
  const [items, setItems] = useState<any[] | null>(null);
  async function load() {
    try {
      const t = getAdminAccessToken();
      const res = await fetch('/api/admin/cricket/exchange/unsettled-markets', { headers: { ...(t ? { authorization: `Bearer ${t}` } : {}) } });
      const d = await res.json();
      if (d.success) setItems(d.items || []);
    } catch { /* transient */ }
  }
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);
  if (!items || items.length === 0) return null;   // healthy → show nothing
  const ICON: Record<string, string> = { cricket: '🏏', football: '⚽', tennis: '🎾' };
  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-6 border border-amber-300 dark:border-amber-700/60 shadow-sm space-y-3">
      <h2 className="text-lg font-bold text-amber-900 dark:text-amber-200">⚠️ {items.length} completed match{items.length === 1 ? '' : 'es'} awaiting settlement</h2>
      <p className="text-xs text-amber-800/80 dark:text-amber-300/70">These finished with open bets. Football &amp; tennis have no result feed — settle them by hand. Any left unsettled past 24h are auto-<b>voided</b> (players refunded), never stranded.</p>
      <div className="space-y-2">
        {items.map((it: any) => (
          <div key={it.matchId} className="flex items-center justify-between gap-3 rounded-lg bg-white/60 dark:bg-gray-900/40 px-3 py-2 border border-amber-200 dark:border-amber-800/50">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{ICON[it.sport] || '🎯'} {it.title || it.matchId}</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">{it.openBets} open bet{it.openBets === 1 ? '' : 's'} · ₹{Number(it.openStake).toLocaleString('en-IN')} at stake · {it.status}</div>
            </div>
            <div className="text-right shrink-0">
              <div className={`text-xs font-bold ${it.hoursUntilAutoVoid != null && it.hoursUntilAutoVoid < 4 ? 'text-red-600' : 'text-amber-700 dark:text-amber-300'}`}>
                {it.hoursUntilAutoVoid == null ? 'no timer' : it.hoursUntilAutoVoid <= 0 ? 'voiding now' : `${it.hoursUntilAutoVoid}h to auto-void`}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// GM-3: per-sport P&L split — each API sport (cricket/football/tennis) on its own line.
function SportPnlControl() {
  const [data, setData] = useState<any[] | null>(null);
  async function load() {
    try {
      const t = getAdminAccessToken();
      const res = await fetch('/api/admin/cricket/exchange/sport-pnl', { headers: { ...(t ? { authorization: `Bearer ${t}` } : {}) } });
      const d = await res.json();
      if (d.success) setData(d.sports);
    } catch { /* transient */ }
  }
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);
  const ICON: Record<string, string> = { cricket: '🏏', football: '⚽', tennis: '🎾' };
  const rows = data || ['cricket', 'football', 'tennis'].map((s) => ({ sport: s }));
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white">📊 P&amp;L by Sport</h2>
      <p className="text-xs text-gray-500 dark:text-gray-400">Each API sport on its own line — cricket, football &amp; tennis share the engine but report independently.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {rows.map((s: any) => (
          <div key={s.sport} className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between"><span className="font-bold text-gray-900 dark:text-white">{ICON[s.sport]} {s.sport[0].toUpperCase() + s.sport.slice(1)}</span><span className="text-[11px] text-gray-400">{s.liveMatches ?? 0} live</span></div>
            <div className={`text-2xl font-bold ${(s.housePnl ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{(s.housePnl ?? 0) >= 0 ? '+' : ''}₹{Number(s.housePnl ?? 0).toLocaleString('en-IN')}</div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">House P&amp;L · {s.openBets ?? 0} open · ₹{Number(s.turnover ?? 0).toLocaleString('en-IN')} turnover</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// GM-5: RM Slider Engine control — per-sport risk posture via 3 sliders + presets.
// Hits /exchange/rm-posture/:sport; the server maps sliders → ~12 knobs (rm_overlay_<sport>).
function KnobStat({ label, v }: { label: string; v: string }) {
  return <div className="bg-gray-50 dark:bg-gray-900/50 rounded p-2"><div className="text-gray-400">{label}</div><div className="font-bold text-gray-800 dark:text-gray-100">{v}</div></div>;
}

function RmSliderControl({ initialSport = 'cricket' }: { initialSport?: 'cricket' | 'football' | 'tennis' }) {
  const SPORTS = ['cricket', 'football', 'tennis'] as const;
  const [sport, setSport] = useState<'cricket' | 'football' | 'tennis'>(initialSport);
  const [posture, setPosture] = useState<{ protection: number; feedTrust: number; friendliness: number }>({ protection: 55, feedTrust: 60, friendliness: 55 });
  const [knobs, setKnobs] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function rmFetch(url: string, options?: RequestInit) {
    const t = getAdminAccessToken();
    const res = await fetch(url, { ...options, headers: { 'Content-Type': 'application/json', ...(t ? { authorization: `Bearer ${t}` } : {}) } });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || d.success === false) throw new Error(d.error || `HTTP ${res.status}`);
    return d;
  }
  async function load(s: string) {
    try { const d = await rmFetch(`/api/admin/cricket/exchange/rm-posture/${s}`); setPosture(d.posture); setKnobs(d.knobs); setMsg(''); } catch (e: any) { setMsg(e.message); }
  }
  useEffect(() => { load(sport); /* eslint-disable-next-line */ }, [sport]);
  async function apply() {
    setBusy(true); setMsg('Applying…');
    try { const d = await rmFetch(`/api/admin/cricket/exchange/rm-posture/${sport}`, { method: 'POST', body: JSON.stringify(posture) }); setKnobs(d.knobs); setMsg(`Applied to ${sport} — live now.`); } catch (e: any) { setMsg(e.message); } finally { setBusy(false); }
  }
  const Slider = ({ label, k, hint }: { label: string; k: 'protection' | 'feedTrust' | 'friendliness'; hint: string }) => (
    <div>
      <div className="flex justify-between text-xs font-bold text-gray-700 dark:text-gray-200"><span>{label}</span><span>{posture[k]}</span></div>
      <input type="range" min={0} max={100} value={posture[k]} onChange={(e) => setPosture((p) => ({ ...p, [k]: Number(e.target.value) }))} className="w-full accent-violet-600" />
      <div className="text-[11px] text-gray-400">{hint}</div>
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-violet-400/40 dark:border-violet-500/30 shadow-sm space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">🎚️ Risk Management</h2>
        <div className="flex gap-1">
          {SPORTS.map((s) => <button key={s} onClick={() => setSport(s)} className={`px-2.5 py-1 text-xs font-bold rounded ${sport === s ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-700'}`}>{s === 'cricket' ? '🏏' : s === 'football' ? '⚽' : '🎾'} {s[0].toUpperCase() + s.slice(1)}</button>)}
        </div>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">Three sliders drive ~12 underlying risk knobs for <b>{sport}</b>, independently of the other sports. <b>Feed-Trust</b> is the untrusted-API defense — lower it when the vendor feed can't be trusted (late / insider data): shorter odds-freshness, longer per-ball suspend, bigger bet-delay.</p>
      {/* C4 (2026-08-06): tell the operator this overlay wins over the global Exchange card. */}
      <p className="text-[11px] text-amber-700 dark:text-amber-300">Heads up: applying this overrides the matching values in the <b>Exchange &amp; Risk Controls</b> card below for <b>{sport}</b> only — for the knobs shown here, this overlay is what wins at bet time.</p>

      <div className="flex flex-wrap gap-2">
        <span className="text-xs font-bold text-gray-500 uppercase mr-1 self-center">Presets:</span>
        <button onClick={() => setPosture({ protection: 80, feedTrust: 35, friendliness: 30 })} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-sm font-bold rounded">Conservative</button>
        <button onClick={() => setPosture({ protection: 55, feedTrust: 60, friendliness: 55 })} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-sm font-bold rounded">Balanced</button>
        <button onClick={() => setPosture({ protection: 30, feedTrust: 80, friendliness: 80 })} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-sm font-bold rounded">Aggressive</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Slider label="🛡️ House Protection" k="protection" hint="↑ tighter ceilings, higher margin" />
        <Slider label="📡 Feed Trust" k="feedTrust" hint="↓ = distrust API: tighter freshness/suspend" />
        <Slider label="😀 Player Friendliness" k="friendliness" hint="↑ looser margins, higher player caps" />
      </div>

      <div className="flex items-center gap-3">
        <button onClick={apply} disabled={busy} className="px-5 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-sm font-bold rounded">Apply to {sport}</button>
        {msg && <span className="text-sm text-gray-600 dark:text-gray-300">{msg}</span>}
      </div>

      {knobs && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] border-t border-gray-100 dark:border-gray-700 pt-3">
          <KnobStat label="House margin" v={`${knobs.house_margin_pct}%`} />
          <KnobStat label="Runner ceiling" v={`₹${Number(knobs.rm_runner_exposure_ceiling).toLocaleString('en-IN')}`} />
          <KnobStat label="Fancy ceiling" v={`₹${Number(knobs.rm_fancy_exposure_ceiling).toLocaleString('en-IN')}`} />
          <KnobStat label="Max bet liability" v={`₹${Number(knobs.rm_max_bet_liability).toLocaleString('en-IN')}`} />
          <KnobStat label="Odds freshness" v={`${knobs.odds_max_staleness_seconds}s`} />
          <KnobStat label="Ball suspend" v={`${knobs.ball_suspend_seconds}s`} />
          <KnobStat label="Bet delay" v={`${knobs.bet_delay_seconds}s`} />
          <KnobStat label="Odds-shift" v={`${knobs.rm_shift_factor}`} />
        </div>
      )}
    </div>
  );
}
